import { EMOTICON_END_REGEX, EMOTICON_REGEX, getEmoticonEmoji } from "./emoji-emoticons";
import { EMOJI_SHORTCODES, findEmojiShortcodes, IEmojiShortcode } from "./emoji-shortcodes";

/**
 * The ":" autocomplete behind a chat composer -- ":wink" offers 😉, and ":wink:" becomes it without
 * anybody having to pick from a list.  Typed faces are its other half: ";)" becomes the same emoji
 * for the people who never stopped typing them.  Built the same way as TeamChatMentionAutocomplete and held
 * here rather than in each page, so the desktop widget and the mobile page behave identically: the
 * component hands it the text and the caret, and gets back what the text should now be.
 *
 * It owns nothing to do with the view, and it never touches the message body on its own -- the
 * composer holds the emoji itself from the moment it's picked, so what was typed is what gets sent.
 */
export class EmojiAutocomplete {
	/** Longest run after a ":" still treated as an emoji name being typed. */
	private static readonly MAX_QUERY_LENGTH = 30;

	/** How many the list shows at once. */
	private static readonly MAX_SUGGESTIONS = 8;

	isOpen: boolean = false;
	matches: IEmojiShortcode[] = [];
	selectedIndex: number = 0;

	/** Where the ":" being completed sits in the text. */
	private startIndex: number = -1;

	/** The ":" the user pressed Esc on -- stays closed until they start a different one. */
	private dismissedIndex: number = -1;

	/**
	 * A name typed in full and closed off -- ":wink:" -- turned into its emoji on the spot, which is
	 * what people who know the name are expecting rather than another list to confirm.  Returns the
	 * text the composer should now hold and where to put the caret, or null when the caret isn't
	 * sitting just after a closed-off name.
	 *
	 * Call this before update(): a completed name is a finished job, not something to keep offering.
	 */
	completeShortcode(text: string, caret: number): { text: string; caret: number } {
		const textBeforeCaret = (text || "").substring(0, caret);

		// The colon that just closed it, and the one that opened the name before it.
		if (textBeforeCaret.endsWith(":") === false)
			return null;

		const openIndex = textBeforeCaret.lastIndexOf(":", textBeforeCaret.length - 2);
		if (openIndex < 0)
			return null;

		if (this.isStartOfName(textBeforeCaret, openIndex) === false)
			return null;

		const name = textBeforeCaret.substring(openIndex + 1, textBeforeCaret.length - 1);
		if (this.isName(name) === false)
			return null;

		const shortcode = EMOJI_SHORTCODES.find(x => x.names.indexOf(name.toLowerCase()) >= 0);
		if (!shortcode)
			return null;

		const newText = text.substring(0, openIndex) + shortcode.emoji + text.substring(caret);

		this.close();

		return { text: newText, caret: openIndex + shortcode.emoji.length };
	}

	/**
	 * A typed face turned into its emoji once it's finished -- ";)" becomes 😉 as the space after it
	 * is typed.  Waiting for that space is what keeps "8-) " a pair of sunglasses and "item 8) do
	 * this" a numbered list: until the face is closed off by something, there's no telling which was
	 * meant, and a replacement that fires early is one the user has to undo.
	 *
	 * Returns the text the composer should now hold and where to put the caret, or null when what's
	 * behind the caret isn't a finished face.
	 */
	completeEmoticon(text: string, caret: number): { text: string; caret: number } {
		const textBeforeCaret = (text || "").substring(0, caret);

		// The whitespace that just closed it off, and the face in front of that.
		const trailingWhitespace = textBeforeCaret.match(/\s+$/);
		if (!trailingWhitespace)
			return null;

		const textBeforeWhitespace = textBeforeCaret.substring(0, textBeforeCaret.length - trailingWhitespace[0].length);

		const match = textBeforeWhitespace.match(EMOTICON_END_REGEX);
		if (!match)
			return null;

		const emoticon = match[2];
		const emoji = getEmoticonEmoji(emoticon);
		if (!emoji)
			return null;

		const startIndex = textBeforeWhitespace.length - emoticon.length;
		const newText = text.substring(0, startIndex) + emoji + text.substring(startIndex + emoticon.length);

		this.close();

		return { text: newText, caret: caret - emoticon.length + emoji.length };
	}

	/** Called as the composer changes: opens, filters or closes the list to match what's been typed. */
	update(text: string, caret: number) {
		const emojiQuery = this.getEmojiQuery(text, caret);

		if (!emojiQuery) {
			this.close();
			return;
		}

		// Esc'd this one already -- leave it closed until they start another ":" somewhere else.
		if (emojiQuery.startIndex === this.dismissedIndex) {
			this.close();
			return;
		}

		this.dismissedIndex = -1;

		const matches = findEmojiShortcodes(emojiQuery.query, EmojiAutocomplete.MAX_SUGGESTIONS);

		// Nothing matching means they're typing a time, a ratio or a plain colon, so get out of the way.
		if (matches.length === 0) {
			this.close();
			return;
		}

		this.matches = matches;
		this.startIndex = emojiQuery.startIndex;
		this.selectedIndex = 0;
		this.isOpen = true;
	}

	moveSelection(offset: number) {
		if (!this.isOpen)
			return;

		const count = this.matches.length;

		this.selectedIndex = ((this.selectedIndex + offset) % count + count) % count;
	}

	get selectedShortcode(): IEmojiShortcode {
		return this.isOpen ? this.matches[this.selectedIndex] : null;
	}

	/**
	 * Drops the picked emoji in over the ":name" that was typed.  No trailing space: an emoji often
	 * ends the sentence, and one that doesn't is followed by whatever gets typed next anyway.
	 */
	select(shortcode: IEmojiShortcode, text: string, caret: number): { text: string; caret: number } {
		if (!shortcode || this.startIndex < 0)
			return { text, caret };

		const newText = text.substring(0, this.startIndex) + shortcode.emoji + text.substring(caret);
		const newCaret = this.startIndex + shortcode.emoji.length;

		this.close();

		return { text: newText, caret: newCaret };
	}

	/** Esc: back to ordinary typing, and this ":" doesn't re-open the list. */
	dismiss() {
		this.dismissedIndex = this.startIndex;
		this.close();
	}

	close() {
		this.isOpen = false;
		this.matches = [];
		this.selectedIndex = 0;
		this.startIndex = -1;
	}

	/** Leaving the conversation (or sending) -- nothing typed carries over. */
	reset() {
		this.dismissedIndex = -1;
		this.close();
	}

	/**
	 * Everything a finished message spells out in text turned into emojis: the ":name:" shortcodes and
	 * the typed faces alike.
	 *
	 * Most of it has been replaced already, as it was typed.  This catches what never went through the
	 * composer -- pasted text -- and the last face in a message, which is sent before anybody types
	 * the space that would have closed it off.
	 */
	applyTextEmojis(body: string): string {
		return this.applyEmoticons(this.applyShortcodes(body));
	}

	/** The ":name:" half of applyTextEmojis. */
	applyShortcodes(body: string): string {
		if (!body || body.indexOf(":") < 0)
			return body;

		return body.replace(/:([a-z0-9_+-]{1,30}):/gi, (match, name) => {
			const shortcode = EMOJI_SHORTCODES.find(x => x.names.indexOf(name.toLowerCase()) >= 0);

			return shortcode ? shortcode.emoji : match;
		});
	}

	/** The typed-face half of applyTextEmojis.  Only faces standing on their own, so a URL keeps its
	 *  "://" and a ratio keeps its colon. */
	applyEmoticons(body: string): string {
		if (!body)
			return body;

		return body.replace(EMOTICON_REGEX, (match, leadingSpace, emoticon) => `${leadingSpace}${getEmoticonEmoji(emoticon) || emoticon}`);
	}

	/**
	 * The ":name" being typed at the caret, if there is one.  A colon mid-word is a time or a URL
	 * ("12:30", "https://"), not somebody reaching for an emoji, so it doesn't count -- and neither
	 * does a bare ":" with nothing after it yet, which is how most sentences use one.
	 */
	private getEmojiQuery(text: string, caret: number): { startIndex: number; query: string } {
		const textBeforeCaret = (text || "").substring(0, caret);
		const colonIndex = textBeforeCaret.lastIndexOf(":");

		if (colonIndex < 0)
			return null;

		if (this.isStartOfName(textBeforeCaret, colonIndex) === false)
			return null;

		const query = textBeforeCaret.substring(colonIndex + 1);

		if (query.length === 0 || this.isName(query) === false)
			return null;

		return { startIndex: colonIndex, query };
	}

	/** Whether the colon at this index opens a name rather than sitting inside a word. */
	private isStartOfName(text: string, colonIndex: number): boolean {
		const characterBefore = (colonIndex === 0) ? " " : text.charAt(colonIndex - 1);

		return /[\s(]/.test(characterBefore);
	}

	/** What an emoji name is made of -- and it doesn't run onto the next line or ramble. */
	private isName(name: string): boolean {
		return name.length <= EmojiAutocomplete.MAX_QUERY_LENGTH && /^[a-z0-9_+-]*$/i.test(name);
	}
}
